import SoundFont from "soundfont-player";
import MidiPlayer from "midi-player-js";
import {
  SUSTAINED_NOTE_DURATION,
  NON_SUSTAINED_NOTE_DURATION
} from "../utils/constants";
import ReverbJS from "reverb.js";

export const EVENT_TYPES = {
  SUSTAIN_PEDAL_PRESSED: "SUSTAIN_PEDAL_PRESSED",
  SUSTAIN_PEDAL_RELEASED: "SUSTAIN_PEDAL_RELEASED",
  KEY_PRESSED: "KEY_PRESSED",
  KEY_RELEASED: "KEY_RELEASED",
  MIDI_LOADING: "MIDI_LOADING",
  MIDI_LOADED: "MIDI_LOADED",
  MIDI_STARTED: "MIDI_STARTED",
  MIDI_STOPPED: "MIDI_STOPPED",
  MIDI_PAUSED: "MIDI_PAUSED",
  INSTRUMENT_LOADED: "INSTRUMENT_LOADED"
};

export class FancyMidiPlayer {
  constructor() {
    // Audio context is required to play Soundfont files and reverb
    this.audioContext =
      window.AudioContext || window.webkitAudioContext || false;
    this.safeAudioContext = new this.audioContext();
    this.instrument = null;
    // MIDI file that will be played
    this.midi = null;
    this.player = null;
    this.subscribers = [];
    this.volume = 3;
    this.sustain = 0;
    this.notesPlaying = {};
    this.isPlaying = false;

    // Debug timing state
    this._debugNoteCount = 0;
    this._debugPlayStartTime = 0;

    // Tick-based audio scheduling state (set during playMidi)
    this._playbackStartAudioTime = null;
    this._tempoMap = null;
    this._division = null;

    // Pre-scheduled audio nodes — all audio is scheduled before playback starts
    // so chords are sample-accurate. midi-player-js is used only for visuals.
    this._scheduledKeyEvents = [];
    this._visualStartTimer = null;
    this._stateMonitor = null;
    // Master gain node: all raw AudioBufferSourceNodes connect here (1 destination
    // connection total instead of one per note, avoiding Chrome's connection limit).
    this._masterGain = null;
    // Rolling scheduler: creates AudioBufferSourceNodes only ~15s ahead at a time.
    // Avoids Chrome's limit on simultaneously active audio nodes for large MIDI files.
    this._schedulerInterval = null;
    this._preprocessedAudio = null;  // { noteOnList, startAudioTime }
    this._nextScheduleIdx = 0;
    // Tick position saved by pauseMidi() so playMidi() can resume from that point
    this._pausedAtTick = null;

    /*
    ReverbJS.extend(this.safeAudioContext);

    // Load the impulse response; upon load, connect it to the audio output.
    const reverbUrl = "./public/reverb/Basement.m4a";
    this.reverbNode = this.safeAudioContext.createReverbFromUrl(
      reverbUrl,
      function () {
        this.reverbNode.connect(this.safeAudioContext.destination);
      }.bind(this)
    );*/
  }

  setSustain(value = 0) {
    this.sustain = value;
    // Notify subscribers for visual sustain pedal feedback
    if (value >= 64) {
      this.notify({ type: EVENT_TYPES.SUSTAIN_PEDAL_PRESSED, context: { intensity: value } });
    } else {
      this.notify({ type: EVENT_TYPES.SUSTAIN_PEDAL_RELEASED, context: { intensity: value } });
    }
  }

  shouldSustainNote() {
    return this.sustain >= 64;
  }

  notify(event) {
    if (this.subscribers.length) {
      this.subscribers.forEach(({ subscriber }, idx) => {
        const t0 = performance.now();
        subscriber.handle(event);
        const t1 = performance.now();
        if ((t1 - t0) > 3) {
          console.warn(`[MIDI-DEBUG] ⚠️ Subscriber #${idx} slow handle(${event.type}): ${(t1 - t0).toFixed(2)}ms`);
        }
      });
    }
  }   

  subscribe(subscriber) {
    if (typeof subscriber.handle !== "function") {
      throw new Error("Subscriber must have a 'handle' method.");
    }
    const subscriberId = this.subscribers.length;
    this.subscribers.push({ subscriber, id: subscriberId });
    console.log("Subscriber added:", subscriber);
    console.log("Total subscribers:", this.subscribers.length); // Debugging
    return subscriberId;
  }

  /**
   * Loads js soundfont file
   * @param {string} instrumentUrl
   */
  async loadInstrument(instrumentUrl) {
    const tStart = performance.now();
    console.log(`[MIDI-DEBUG] loadInstrument start: ${instrumentUrl}`);

    // Pre-warm the AudioContext immediately so it's running before the user presses play
    try { await this.safeAudioContext.resume(); } catch(e) {}
    console.log(`[MIDI-DEBUG] AudioContext state after resume: ${this.safeAudioContext.state}`);

    const tBeforeSF = performance.now();
    this.instrument = await SoundFont.instrument(
      this.safeAudioContext,
      instrumentUrl,
      {} // no custom destination - use default audioContext.destination
    );
    console.log(`[MIDI-DEBUG] SoundFont.instrument() loaded in ${(performance.now() - tBeforeSF).toFixed(0)}ms`);

    this.notify({ type: EVENT_TYPES.INSTRUMENT_LOADED });

    // Attach event handlers to midi player
    this.player = new MidiPlayer.Player((event) => {
      const tEventStart = performance.now();
      if (event.name === "Controller Change") {
        this.onControllerChange(event);
      } else if (event.name === "Set Tempo") {
        console.log(`[MIDI-DEBUG] Set Tempo event: BPM=${event.data}, tick=${event.tick}`);
      } else if (event.name === "Note on") {
        this.onNoteOnEvent(event);
      } else if (event.name === "Note off") {
        this.onNoteOffEvent(event);
      }
      const tEventEnd = performance.now();
      // Log if the entire event callback (including all handler work) is slow
      if ((tEventEnd - tEventStart) > 10) {
        console.warn(`[MIDI-DEBUG] ⚠️ SLOW event callback: ${event.name} ${event.noteName || ''} took ${(tEventEnd - tEventStart).toFixed(2)}ms`);
      }
    });
    console.log(`[MIDI-DEBUG] loadInstrument total: ${(performance.now() - tStart).toFixed(0)}ms`);
  }

  onControllerChange(event) {
    // Handle sustain pedal event
    if (event.number === 64) {
      const t0 = performance.now();
      this.setSustain(event.value);
      const t1 = performance.now();
      console.log(`[MIDI-DEBUG] Controller Change (sustain=${event.value}) took ${(t1 - t0).toFixed(2)}ms`);
    }
  }

  onNoteOnEvent(event) {
    // Visuals are pre-scheduled via setTimeout in _scheduleWindow — ignore midi-player-js events
  }

  onNoteOffEvent(event) {
    // Visuals are pre-scheduled via setTimeout in _scheduleWindow — ignore midi-player-js events
  }

  // ─── Tick → seconds helpers ──────────────────────────────────────────────────
  // These compute exact audio-scheduled times from MIDI tick positions.
  // Used by onNoteOnEvent / onNoteOffEvent so all notes at the same tick
  // always produce the same audioTime, regardless of setInterval jitter.
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Builds a sorted list of tempo changes from all MIDI track events.
   * Returns [{tick, microsPerBeat}] — the tempo at each tick position.
   * Defaults to 120 BPM (500000 µs/beat) if no Set Tempo events found.
   *
   * midi-player-js quirk: in the live event callback, event.data is BPM (e.g. 80).
   * But in getEvents() raw data, event.data may be µs/beat (e.g. 750000).
   * We detect which by the value: µs/beat for BPM 20–300 is in range 200000–3000000.
   * BPM values are 20–300. So if event.data > 3000 we treat it as µs/beat directly.
   */
  _buildTempoMap(allTrackEvents) {
    const tempoChanges = [];
    allTrackEvents.forEach(trackEvents => {
      trackEvents.forEach(event => {
        if (event.name === 'Set Tempo' && typeof event.data === 'number' && event.data > 0) {
          let microsPerBeat;
          if (event.data > 3000) {
            // Raw µs/beat value (e.g. 750000 = 80 BPM)
            microsPerBeat = Math.round(event.data);
            console.log(`[MIDI-DEBUG] Set Tempo (raw µs/beat): ${event.data} → ${(60e6 / event.data).toFixed(1)} BPM at tick=${event.tick}`);
          } else {
            // BPM value (e.g. 80)
            microsPerBeat = Math.round(60e6 / event.data);
            console.log(`[MIDI-DEBUG] Set Tempo (BPM): ${event.data} BPM → ${microsPerBeat} µs/beat at tick=${event.tick}`);
          }
          tempoChanges.push({ tick: event.tick, microsPerBeat });
        }
      });
    });
    tempoChanges.sort((a, b) => a.tick - b.tick);
    // Ensure there is always a tempo entry at tick 0
    if (tempoChanges.length === 0 || tempoChanges[0].tick > 0) {
      tempoChanges.unshift({ tick: 0, microsPerBeat: 500000 }); // 120 BPM default
    }
    return tempoChanges;
  }

  /**
   * Converts a MIDI tick position to seconds, respecting all tempo changes.
   * @param {number} tick  - absolute MIDI tick
   * @param {Array}  tempoMap - from _buildTempoMap()
   * @param {number} division - ticks per quarter note (from MIDI header)
   */
  _tickToSeconds(tick, tempoMap, division) {
    let seconds = 0;
    let prevTick = 0;
    let prevMicros = tempoMap[0].microsPerBeat;

    for (let i = 1; i < tempoMap.length; i++) {
      const change = tempoMap[i];
      if (change.tick >= tick) break;
      seconds += (change.tick - prevTick) * (prevMicros / 1e6) / division;
      prevTick = change.tick;
      prevMicros = change.microsPerBeat;
    }
    seconds += (tick - prevTick) * (prevMicros / 1e6) / division;
    return seconds;
  }

  /**
   * Pre-schedules ALL note audio events using the Web Audio API, processed in async chunks
   * so the main thread is never blocked and the audio engine isn't overwhelmed.
   *
   * Why pre-scheduling instead of scheduling on each setInterval callback:
   *   midi-player-js fires ONE event per setInterval callback. A 5-note chord fires 5
   *   separate callbacks, each ~60ms apart. Scheduling audio inside each callback means
   *   notes in the same chord get different audioTimes (drifting by up to 400+ms).
   *   Pre-scheduling computes all audioTimes from a fixed formula before playback starts,
   *   so every note in a chord gets the EXACT SAME audioTime — sample-accurate.
   *
   * midi-player-js still runs during playback but ONLY for visual events
   * (painting piano keys via onNoteOnEvent/onNoteOffEvent notifications).
   *
   * @param {number} startAudioTime   - absolute audioContext time when the first resumed note should play
   * @param {Array}  allTrackEvents   - from player.getEvents() — pre-fetched by the caller
   * @param {Array}  tempoMap         - from _buildTempoMap() — pre-built by the caller
   * @param {number} resumeFromTick   - skip notes before this tick and offset audio times (0 = fresh start)
   */
  async _preScheduleAllAudio(startAudioTime, allTrackEvents, tempoMap, resumeFromTick = 0) {
    const tStart = performance.now();
    const division = this.player.division;

    const bpm = (60e6 / tempoMap[0].microsPerBeat).toFixed(1);
    console.log(
      `[MIDI-DEBUG] Pre-scheduling audio: ${bpm} BPM, division=${division}, ` +
      `startAudioTime=${startAudioTime.toFixed(3)}s, audioCtx.state=${this.safeAudioContext.state}`
    );

    // Create a single master gain node for this playback session.
    // All raw AudioBufferSourceNodes connect to this instead of directly to destination,
    // keeping the destination connection count at 1 regardless of note count.
    this._masterGain = this.safeAudioContext.createGain();
    this._masterGain.gain.value = 1.0;
    this._masterGain.connect(this.safeAudioContext.destination);

    // Flatten all note events from all tracks into one sorted-by-tick list
    const allEvents = allTrackEvents.flat()
      .filter(e => e.name === 'Note on' || e.name === 'Note off')
      .sort((a, b) => a.tick - b.tick || (a.name === 'Note off' ? -1 : 1)); // note-offs before note-ons at same tick

    console.log(`[MIDI-DEBUG] Total note events to schedule: ${allEvents.length}`);
    if (allEvents.length > 0) {
      const sampleEvent = allEvents[0];
      console.log(`[MIDI-DEBUG] Sample event[0]:`, JSON.stringify({
        name: sampleEvent.name, tick: sampleEvent.tick,
        noteName: sampleEvent.noteName, noteNumber: sampleEvent.noteNumber,
        velocity: sampleEvent.velocity, channel: sampleEvent.channel
      }));
    }

    // ── PRE-PAIR note-on with note-off to compute exact duration ──────────
    // Why: soundfont-player's keyEvent.stop(futureTime) reads gain.value at
    // JS execution time (while the note is still silence / attack phase) and
    // writes that 0-value back into gain automation at the future stop time,
    // silencing the note before it starts. Passing `duration` to instrument.play()
    // avoids this entirely: soundfont-player does stop(when + duration + release),
    // which is always relative to the note's own start time.
    const noteOffTickByKey = {}; // noteKey → note-off tick (closest upcoming one)
    // Build a map: for each note-on, find the next note-off on the same key+channel
    // We iterate forward to pair them in order
    const pendingNoteOns = {}; // noteKey → {index in allEvents}
    const noteDurations = new Map(); // event-index → duration in seconds
    allEvents.forEach((event, idx) => {
      const noteKey = `${event.noteName}_ch${event.channel ?? 0}`;
      if (event.name === 'Note on' && event.velocity > 0) {
        // If there's already a pending note-on for this key, close it now (no off found)
        pendingNoteOns[noteKey] = idx;
      } else if (event.name === 'Note off' || (event.name === 'Note on' && event.velocity === 0)) {
        if (pendingNoteOns[noteKey] !== undefined) {
          const onIdx = pendingNoteOns[noteKey];
          const onTick = allEvents[onIdx].tick;
          const offTick = event.tick;
          const durSecs = this._tickToSeconds(offTick, tempoMap, division) -
                          this._tickToSeconds(onTick, tempoMap, division);
          noteDurations.set(onIdx, Math.max(0.05, durSecs)); // at least 50ms
          delete pendingNoteOns[noteKey];
        }
      }
    });
    // Any unpaired note-ons get a default duration
    Object.values(pendingNoteOns).forEach(idx => {
      noteDurations.set(idx, SUSTAINED_NOTE_DURATION);
    });

    // ── Build pre-computed note list (no AudioNodes created yet) ─────────────
    // Pre-compute audioTime + duration for every Note on, stored in noteOnList.
    // The rolling scheduler (_scheduleWindow) then creates AudioBufferSourceNodes
    // only ~15s ahead at a time. This keeps the active audio node count low and
    // avoids Chrome silently dropping nodes when scheduling all 2739 at once.
    // When resuming from pause: only schedule notes at or after resumeFromTick,
    // offset by the time already elapsed so the next note plays at startAudioTime.
    const resumeOffsetSecs = resumeFromTick > 0
      ? this._tickToSeconds(resumeFromTick, tempoMap, division)
      : 0;

    const noteOnList = [];
    allEvents.forEach((event, idx) => {
      if (event.name === 'Note on' && event.velocity > 0) {
        if (event.tick < resumeFromTick) return; // already past — skip
        const audioTime = startAudioTime
          + this._tickToSeconds(event.tick, tempoMap, division)
          - resumeOffsetSecs;
        const duration = noteDurations.get(idx) ?? SUSTAINED_NOTE_DURATION;
        noteOnList.push({ event, audioTime, duration });
      }
    });

    const firstAt = noteOnList.length > 0 ? noteOnList[0].audioTime - startAudioTime : null;
    const lastAt  = noteOnList.length > 0 ? noteOnList[noteOnList.length - 1].audioTime - startAudioTime : null;
    const elapsed = (performance.now() - tStart).toFixed(0);
    console.log(
      `[MIDI-DEBUG] Preprocessed ${noteOnList.length} note-ons in ${elapsed}ms. ` +
      `First note at +${firstAt !== null ? firstAt.toFixed(3) : 'N/A'}s, ` +
      `last at +${lastAt !== null ? lastAt.toFixed(3) : 'N/A'}s, ` +
      `audioContext.now=${this.safeAudioContext.currentTime.toFixed(3)}s (state=${this.safeAudioContext.state})`
    );
    if (firstAt !== null && firstAt > 0.5) {
      console.warn(
        `[MIDI-DEBUG] ⚠️ SILENCE AT START: The MIDI file has ${firstAt.toFixed(2)}s of silence before the ` +
        `first note. After pressing Play, wait ~${(firstAt + 0.6).toFixed(1)}s before expecting to hear anything.`
      );
    }

    this._scheduledKeyEvents = [];
    this._preprocessedAudio = { noteOnList, startAudioTime };
    this._nextScheduleIdx = 0;

    // Schedule the first 15 seconds of audio synchronously (just a few hundred nodes max)
    const INITIAL_AHEAD_S = 15.0;
    this._scheduleWindow(startAudioTime + INITIAL_AHEAD_S);

    // Rolling refill: every 5s, add the next 15s of notes.
    // Keeps active AudioNode count at ~150-300 max regardless of song length.
    const REFILL_INTERVAL_MS = 5000;
    const REFILL_AHEAD_S = 15.0;
    this._schedulerInterval = setInterval(() => {
      if (!this.isPlaying) {
        clearInterval(this._schedulerInterval);
        this._schedulerInterval = null;
        return;
      }
      this._scheduleWindow(this.safeAudioContext.currentTime + REFILL_AHEAD_S);
    }, REFILL_INTERVAL_MS);
  }

  /**
   * Creates AudioBufferSourceNodes for pre-processed notes whose audioTime ≤ scheduleUntilAudioTime.
   * Advances this._nextScheduleIdx so each call picks up where the previous left off.
   * Called once synchronously at the start (first 15s), then periodically by the rolling scheduler.
   */
  _scheduleWindow(scheduleUntilAudioTime) {
    if (!this._preprocessedAudio || !this._masterGain) return;
    const { noteOnList } = this._preprocessedAudio;
    let scheduled = 0;

    while (this._nextScheduleIdx < noteOnList.length) {
      const { event, audioTime, duration } = noteOnList[this._nextScheduleIdx];
      if (audioTime > scheduleUntilAudioTime) break;

      if (!event.noteName) {
        console.warn(`[MIDI-DEBUG] Note on event missing noteName at tick=${event.tick}`, event);
        this._nextScheduleIdx++;
        continue;
      }

      // ── Bypass soundfont-player's ADSR (gain bug for far-future notes) ────
      const buffer = this.instrument.buffers[event.noteNumber];
      if (!buffer) {
        // Fallback: let soundfont-player pitch-shift from the nearest available sample
        try {
          const keyEvent = this.instrument.play(event.noteName, audioTime, {
            gain: (event.velocity / 100) * this.volume,
          });
          if (keyEvent) {
            if (keyEvent.source) { try { keyEvent.source.stop(audioTime + duration + 0.3); } catch(e) {} }
            this._scheduledKeyEvents.push(keyEvent);
          }
        } catch(err) { console.error(`[MIDI-DEBUG] Fallback play threw for ${event.noteName}:`, err); }
        this._nextScheduleIdx++;
        scheduled++;
        continue;
      }

      try {
        const source = this.safeAudioContext.createBufferSource();
        source.buffer = buffer;
        const gainNode = this.safeAudioContext.createGain();
        gainNode.gain.value = (event.velocity / 100) * this.volume;
        source.connect(gainNode);
        gainNode.connect(this._masterGain);
        source.start(audioTime);
        source.stop(audioTime + duration + 0.3); // +0.3s natural release tail

        // ── Pre-schedule visual key highlight in sync with audio ──────────
        // Use (audioTime - audioCtx.currentTime) so the delay is measured from
        // right now, matching the audio engine's own scheduling offset.
        const noteNumber = event.noteNumber;
        const pressDelay  = Math.max(0, (audioTime - this.safeAudioContext.currentTime) * 1000);
        const releaseDelay = Math.max(0, (audioTime + duration - this.safeAudioContext.currentTime) * 1000);
        const pressTimer  = setTimeout(() => {
          if (this.isPlaying) this.notify({ type: EVENT_TYPES.KEY_PRESSED,  context: { noteNumber } });
        }, pressDelay);
        const releaseTimer = setTimeout(() => {
          this.notify({ type: EVENT_TYPES.KEY_RELEASED, context: { noteNumber } });
        }, releaseDelay);

        this._scheduledKeyEvents.push({ source, gainNode, pressTimer, releaseTimer,
          stop: (t) => {
            try { source.stop(t || 0); } catch(e) {}
            clearTimeout(pressTimer);
            clearTimeout(releaseTimer);
          }
        });
        scheduled++;
      } catch (err) {
        console.error(`[MIDI-DEBUG] Raw scheduling threw for note=${event.noteName}:`, err);
      }
      this._nextScheduleIdx++;
    }

    if (scheduled > 0) {
      console.log(
        `[MIDI-DEBUG] _scheduleWindow: +${scheduled} notes ` +
        `(${this._nextScheduleIdx}/${noteOnList.length} total), until t=${scheduleUntilAudioTime.toFixed(2)}s`
      );
    }
  }

  async setMidi(midiUrlOrBuffer, customFetch = null) {
    this.notify({ type: EVENT_TYPES.MIDI_LOADING });
    
    try {
      let midiUrl = midiUrlOrBuffer;
      
      // Check if we received an ArrayBuffer directly
      if (midiUrlOrBuffer instanceof ArrayBuffer) {
        console.log("📊 Using provided ArrayBuffer, size:", midiUrlOrBuffer.byteLength, "bytes");
        this.midi = midiUrlOrBuffer;
      } else {
        console.log("📥 Fetching MIDI data from:", midiUrl);
        
        // Use custom fetch function if provided (for authentication), otherwise use regular fetch
        const fetchFunction = customFetch || fetch;
        const response = await fetchFunction(midiUrl);
        
        // Handle different response types based on fetch function used
        if (response.ok === undefined) {
          // This is likely an axios response
          this.midi = response.data;
          console.log("📊 MIDI data loaded via custom fetch, size:", this.midi.byteLength, "bytes");
        } else {
          // This is a regular fetch response
          if (!response.ok) {
            throw new Error(`Failed to fetch MIDI file: ${response.status} ${response.statusText}`);
          }
          this.midi = await response.arrayBuffer();
          console.log("📊 MIDI data loaded via regular fetch, size:", this.midi.byteLength, "bytes");
        }
      }
      
      // Load directly into midi-player-js
      console.log("🔄 Loading MIDI with midi-player-js...");
      this.player.loadArrayBuffer(this.midi);
      
      const tracks = this.player.getEvents();
      const totalEvents = this.player.totalEvents;
      const totalTicks = this.player.totalTicks;
      const firstBytes = Array.from(new Uint8Array(this.midi).slice(0, 8)).map(b => b.toString(16).padStart(2,'0')).join(' ');
      console.log("🎵 MIDI parsed:", tracks.length, "tracks,", totalEvents, "events,", totalTicks, "ticks. First bytes:", firstBytes);
      
      this.notify({ type: EVENT_TYPES.MIDI_LOADED, context: { midiUrl: null } });

      // Pre-build tempo map + division so getPositionSecs/getTotalDurationSecs
      // work immediately after loading (before playMidi is called).
      this._division = this.player.division;
      this._tempoMap = this._buildTempoMap(this.player.getEvents());

    } catch (error) {
      console.error("❌ Error loading MIDI:", error);
      throw error;
    }
  }
  
  async playMidi() {
    console.log("🎮 Starting playback...");
    const tPlayStart = performance.now();

    if (!this.player) {
      console.error("❌ No MIDI player available");
      return;
    }

    // Resume AudioContext — starts suspended due to browser autoplay policy.
    if (this.safeAudioContext.state !== 'running') {
      console.log(`⏳ AudioContext is '${this.safeAudioContext.state}', resuming...`);
      const tResumeStart = performance.now();
      await this.safeAudioContext.resume();
      console.log(`[MIDI-DEBUG] AudioContext resume took ${(performance.now() - tResumeStart).toFixed(2)}ms`);
    }

    // Read resume-from position BEFORE resetting state
    const resumeFromTick = this._pausedAtTick ?? 0;
    this._pausedAtTick = null;
    if (resumeFromTick > 0) {
      console.log(`[MIDI-DEBUG] ▶️ Resuming from tick=${resumeFromTick}`);
    }

    // Reset state
    this._debugNoteCount = 0;
    this._debugPlayStartTime = performance.now();
    this.isPlaying = true;
    this.notesPlaying = {};

    // ── Build tempo map ──────────────────────────────────────────────────────
    const allTrackEvents = this.player.getEvents();
    this._tempoMap = this._buildTempoMap(allTrackEvents);
    this._division = this.player.division;

    // ── Lookahead buffer ───────────────────────────────────────────────────
    // Pre-scheduling creates AudioBufferSource nodes which can take 200-400ms.
    // We schedule all audio from LOOKAHEAD seconds in the future so that:
    //   1. Audio is fully pre-scheduled before the first note sounds
    //   2. We can delay player.play() (visuals) by the same amount so audio
    //      and visuals are in sync at the moment playback begins.
    const LOOKAHEAD_S = 0.6; // 600ms — enough margin for pre-scheduling
    const tLookaheadPerf = performance.now(); // wall-clock anchor (same clock as setTimeout)
    const now = this.safeAudioContext.currentTime;
    const startAudioTime = now + LOOKAHEAD_S;
    this._playbackStartAudioTime = startAudioTime;

    console.log(
      `[MIDI-DEBUG] Player state: division=${this._division}, ` +
      `tempo=${this.player.tempo}, totalTicks=${this.player.totalTicks}, ` +
      `totalEvents=${this.player.totalEvents}`
    );
    console.log(`[MIDI-DEBUG] Tempo map: ${JSON.stringify(this._tempoMap)}`);
    console.log(`[MIDI-DEBUG] startAudioTime = ${startAudioTime.toFixed(3)}s (now + ${LOOKAHEAD_S}s)`);

    // ── PRE-SCHEDULE ALL AUDIO ────────────────────────────────────────────
    // Schedule every note-on and note-off with exact tick-based audio times.
    // All notes in a chord get the EXACT SAME audioTime → sample-accurate chords.
    // Pass pre-fetched events + tempoMap to avoid a second getEvents() call inside.
    await this._preScheduleAllAudio(startAudioTime, allTrackEvents, this._tempoMap, resumeFromTick);

    // Check if stop was pressed during pre-scheduling
    if (!this.isPlaying) {
      console.log('[MIDI-DEBUG] Playback cancelled during pre-scheduling');
      return;
    }

    const preSchedMs = performance.now() - tPlayStart;
    console.log(`[MIDI-DEBUG] Pre-scheduling done in ${preSchedMs.toFixed(0)}ms`);

    // ── Delay player.play() so visuals fire exactly when audio starts ──────
    // Polling the audio clock is more accurate than a fixed setTimeout because
    // AudioContext.currentTime and performance.now() run on different clocks and
    // can diverge by 200ms+ in the first seconds after resume().
    // We poll every 5ms until the audio clock reaches startAudioTime, then start.
    const pollIntervalMs = 5;
    const checkAndPlay = () => {
      if (!this.isPlaying) return; // stop pressed while waiting

      const audioNow = this.safeAudioContext.currentTime;
      if (audioNow >= startAudioTime - pollIntervalMs / 1000) {
        // Audio clock has (nearly) reached startAudioTime — start visuals now
        console.log(
          `▶️ player.play() (visuals) | audioContext.currentTime=${audioNow.toFixed(3)}s | ` +
          `target was ${startAudioTime.toFixed(3)}s | error=${((audioNow - startAudioTime) * 1000).toFixed(1)}ms`
        );
        this.player.play();
        this.notify({ type: EVENT_TYPES.MIDI_STARTED });
      } else {
        this._visualStartTimer = setTimeout(checkAndPlay, pollIntervalMs);
      }
    };

    const elapsedMs = performance.now() - tLookaheadPerf;
    const remainingEstimateMs = Math.max(0, LOOKAHEAD_S * 1000 - elapsedMs - 30); // start polling 30ms early
    console.log(
      `[MIDI-DEBUG] Will start polling at ${remainingEstimateMs.toFixed(0)}ms ` +
      `(elapsed=${elapsedMs.toFixed(0)}ms). First note in song at +2.625s, expect audio at ~${(LOOKAHEAD_S + 2.625).toFixed(1)}s from now.`
    );

    // Monitor AudioContext state — if the browser suspends it, pre-scheduled audio goes silent
    this._stateMonitor = setInterval(() => {
      if (!this.isPlaying) { clearInterval(this._stateMonitor); this._stateMonitor = null; return; }
      const state = this.safeAudioContext.state;
      const t = this.safeAudioContext.currentTime;
      if (state !== 'running') {
        console.error(`[MIDI-DEBUG] ❌ AudioContext SUSPENDED during playback at t=${t.toFixed(3)}s! Attempting resume...`);
        this.safeAudioContext.resume().then(() =>
          console.log(`[MIDI-DEBUG] AudioContext resumed, state=${this.safeAudioContext.state}`)
        );
      } else {
        console.log(`[MIDI-DEBUG] AudioContext OK: t=${t.toFixed(3)}s, state=${state}`);
      }
    }, 2000);
    this._visualStartTimer = setTimeout(checkAndPlay, remainingEstimateMs);

    console.log(`[MIDI-DEBUG] Total playMidi() setup took ${(performance.now() - tPlayStart).toFixed(0)}ms`);
  }

  stopMidi() {
    console.log("🛑 Stopping MIDI playback...");
    this.isPlaying = false;
    this._pausedAtTick = null; // full stop — next play restarts from the beginning

    // Cancel pending visual-start timer (if stop pressed during pre-scheduling)
    clearTimeout(this._visualStartTimer);
    this._visualStartTimer = null;
    clearInterval(this._stateMonitor);
    this._stateMonitor = null;
    if (this._schedulerInterval) { clearInterval(this._schedulerInterval); this._schedulerInterval = null; }
    if (this._masterGain) { try { this._masterGain.disconnect(); } catch(e) {} this._masterGain = null; }
    this._preprocessedAudio = null;
    this._nextScheduleIdx = 0;

    // Clear scheduling state
    this._playbackStartAudioTime = null;
    this._tempoMap = null;
    this._division = null;

    // Stop the midi-player-js visual event loop
    if (this.player) {
      try { this.player.stop(); } catch (e) { /* ignore */ }
    }

    // Cancel ALL pre-scheduled audio nodes immediately
    if (this._scheduledKeyEvents && this._scheduledKeyEvents.length > 0) {
      console.log(`[MIDI-DEBUG] Cancelling ${this._scheduledKeyEvents.length} pre-scheduled audio nodes`);
      this._scheduledKeyEvents.forEach(keyEvent => {
        try { if (keyEvent.source) keyEvent.source.stop(0); } catch (e) { /* already stopped */ }
        try { keyEvent.stop(0); } catch (e) { /* ignore */ }
      });
      this._scheduledKeyEvents = [];
    }

    // Visual cleanup — release all keys
    this.notesPlaying = {};
    this.notify({ type: EVENT_TYPES.MIDI_STOPPED });
  }

  pauseMidi() {
    console.log("⏸️ Pausing MIDI playback...");
    this.isPlaying = false;

    // Save current position so playMidi() can resume from here.
    // player.tick is updated each setInterval loop iteration — most accurate source.
    this._pausedAtTick = this.player ? (this.player.tick || 0) : 0;
    console.log(`[MIDI-DEBUG] Paused at tick=${this._pausedAtTick}`);

    // Cancel pending visual-start timer
    clearTimeout(this._visualStartTimer);
    this._visualStartTimer = null;
    clearInterval(this._stateMonitor);
    this._stateMonitor = null;
    if (this._schedulerInterval) { clearInterval(this._schedulerInterval); this._schedulerInterval = null; }
    if (this._masterGain) { try { this._masterGain.disconnect(); } catch(e) {} this._masterGain = null; }
    this._preprocessedAudio = null;
    this._nextScheduleIdx = 0;

    // Stop the midi-player-js visual event loop
    if (this.player) {
      try { this.player.pause(); } catch (e) {}
    }

    // Cancel ALL pre-scheduled audio nodes immediately
    if (this._scheduledKeyEvents && this._scheduledKeyEvents.length > 0) {
      console.log(`[MIDI-DEBUG] Cancelling ${this._scheduledKeyEvents.length} pre-scheduled audio nodes on pause`);
      this._scheduledKeyEvents.forEach(keyEvent => {
        try { if (keyEvent.source) keyEvent.source.stop(0); } catch (e) { /* already stopped */ }
        try { keyEvent.stop(0); } catch (e) { /* ignore */ }
      });
      this._scheduledKeyEvents = [];
    }

    this.notify({ type: EVENT_TYPES.MIDI_PAUSED });
  }

  /** Public wrapper — converts a tick value to seconds using the current tempo map. */
  tickToSeconds(tick) {
    if (!this._tempoMap || !this._division) return 0;
    return this._tickToSeconds(tick, this._tempoMap, this._division);
  }

  /** Returns current playback position in seconds. */
  getPositionSecs() {
    if (!this._tempoMap || !this._division) return 0;
    const tick = this.player?.tick ?? 0;
    return this._tickToSeconds(tick, this._tempoMap, this._division);
  }

  /** Returns total song duration in seconds. */
  getTotalDurationSecs() {
    if (!this.player || !this._tempoMap || !this._division) return 0;
    return this._tickToSeconds(this.player.totalTicks, this._tempoMap, this._division);
  }

  /**
   * Seeks playback forward or backward by deltaSecs seconds.
   * If currently playing, cancels the current audio and restarts from the new position.
   */
  async seekBySeconds(deltaSecs) {
    if (!this.player || !this._tempoMap || !this._division) return;

    const totalTicks = this.player.totalTicks;
    const currentTick = this.player.tick ?? 0;
    const currentSecs  = this._tickToSeconds(currentTick, this._tempoMap, this._division);
    const totalSecs    = this._tickToSeconds(totalTicks,   this._tempoMap, this._division);
    const targetSecs   = Math.max(0, Math.min(totalSecs - 0.1, currentSecs + deltaSecs));

    // Binary-search for the tick whose time is closest to targetSecs
    let lo = 0, hi = totalTicks;
    while (hi - lo > 1) {
      const mid = Math.floor((lo + hi) / 2);
      if (this._tickToSeconds(mid, this._tempoMap, this._division) <= targetSecs) lo = mid;
      else hi = mid;
    }
    const newTick = lo;
    console.log(`[MIDI-DEBUG] Seek: ${currentSecs.toFixed(1)}s → ${targetSecs.toFixed(1)}s (tick ${currentTick} → ${newTick})`);

    const wasPlaying = this.isPlaying;

    // Cancel all current audio scheduling
    this.isPlaying = false;
    clearTimeout(this._visualStartTimer);  this._visualStartTimer = null;
    clearInterval(this._stateMonitor);     this._stateMonitor = null;
    if (this._schedulerInterval) { clearInterval(this._schedulerInterval); this._schedulerInterval = null; }
    if (this._masterGain) { try { this._masterGain.disconnect(); } catch(e) {} this._masterGain = null; }
    this._preprocessedAudio = null;
    this._nextScheduleIdx = 0;
    this._scheduledKeyEvents.forEach(kev => {
      try { if (kev.source) kev.source.stop(0); } catch(e) {}
      try { kev.stop(0); } catch(e) {}
    });
    this._scheduledKeyEvents = [];

    // Reposition the visual player (skipToTick calls stop() + sets startTick + rewinds track indexes)
    try { this.player.skipToTick(newTick); } catch(e) { console.warn('[MIDI-DEBUG] skipToTick error:', e); }
    this.notify({ type: EVENT_TYPES.MIDI_STOPPED });

    // playMidi will resume from newTick
    this._pausedAtTick = newTick;

    if (wasPlaying) {
      await this.playMidi();
    }
  }

  /**
   * Plays a single note immediately — used for interactive piano key clicks.
   * Uses the same raw AudioBuffer as MIDI playback (bypasses ADSR).
   */
  playNote(noteNumber, velocity = 80) {
    if (!this.instrument) return;
    const ac = this.safeAudioContext;
    if (ac.state !== 'running') ac.resume();

    const gainNode = ac.createGain();
    gainNode.gain.value = (velocity / 100) * this.volume;
    gainNode.connect(ac.destination);

    const buffer = this.instrument.buffers[noteNumber];
    if (buffer) {
      const source = ac.createBufferSource();
      source.buffer = buffer;
      source.connect(gainNode);
      source.start(ac.currentTime);
      source.stop(ac.currentTime + NON_SUSTAINED_NOTE_DURATION);
    } else {
      // Fallback: let soundfont-player pitch-shift from the nearest sample
      const NOTE_NAMES = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
      const noteName = NOTE_NAMES[noteNumber % 12] + (Math.floor(noteNumber / 12) - 1);
      try {
        const kev = this.instrument.play(noteName, ac.currentTime, {
          gain: (velocity / 100) * this.volume,
        });
        if (kev?.source) kev.source.stop(ac.currentTime + NON_SUSTAINED_NOTE_DURATION);
      } catch(e) { /* ignore */ }
    }

    // Visual key highlight for the duration of the note
    this.notify({ type: EVENT_TYPES.KEY_PRESSED,  context: { noteNumber } });
    setTimeout(() => {
      this.notify({ type: EVENT_TYPES.KEY_RELEASED, context: { noteNumber } });
    }, Math.round((NON_SUSTAINED_NOTE_DURATION - 0.3) * 1000));
  }
}
